import { useState } from "react";
import { ComposableMap, Geographies, Geography, Marker } from "react-simple-maps";
import { useTranslation } from "react-i18next";

const GEO_URL = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

// ISO Alpha-3 codes (react-simple-maps uses ISO_A3)
const ACTIVE_A3 = new Set(["NGA", "GHA", "KEN", "ZAF", "UGA", "TZA", "RWA", "CMR", "SEN", "CIV", "ZMB", "BWA", "NAM", "ZWE", "ETH"]);
const EXPANSION_A3 = new Set(["ARE", "SAU", "EGY", "MAR", "USA"]);
const HQ_A3 = new Set(["GBR"]);

const NAME_MAP = {
  NGA: "Nigeria", GHA: "Ghana", KEN: "Kenya", ZAF: "South Africa", UGA: "Uganda", TZA: "Tanzania",
  RWA: "Rwanda", CMR: "Cameroon", SEN: "Senegal", CIV: "Ivory Coast", ZMB: "Zambia", BWA: "Botswana",
  NAM: "Namibia", ZWE: "Zimbabwe", ETH: "Ethiopia",
  ARE: "United Arab Emirates", SAU: "Saudi Arabia", EGY: "Egypt", MAR: "Morocco", USA: "United States",
  GBR: "United Kingdom (HQ)",
};

// Approx [longitude, latitude] for market pins
const MARKERS = [
  { code: "GBR", name: "Manchester HQ", coords: [-2.24, 53.48], kind: "hq" },
  { code: "NGA", name: "Nigeria", coords: [8.68, 9.08], kind: "active" },
  { code: "GHA", name: "Ghana", coords: [-1.03, 7.95], kind: "active" },
  { code: "KEN", name: "Kenya", coords: [37.9, 0.02], kind: "active" },
  { code: "ZAF", name: "South Africa", coords: [22.94, -30.56], kind: "active" },
  { code: "UGA", name: "Uganda", coords: [32.29, 1.37], kind: "active" },
  { code: "TZA", name: "Tanzania", coords: [34.89, -6.37], kind: "active" },
  { code: "RWA", name: "Rwanda", coords: [29.87, -1.94], kind: "active" },
  { code: "CMR", name: "Cameroon", coords: [12.35, 7.37], kind: "active" },
  { code: "SEN", name: "Senegal", coords: [-14.45, 14.5], kind: "active" },
  { code: "CIV", name: "Ivory Coast", coords: [-5.55, 7.54], kind: "active" },
  { code: "ZMB", name: "Zambia", coords: [27.85, -13.13], kind: "active" },
  { code: "BWA", name: "Botswana", coords: [24.68, -22.33], kind: "active" },
  { code: "NAM", name: "Namibia", coords: [18.49, -22.96], kind: "active" },
  { code: "ZWE", name: "Zimbabwe", coords: [29.15, -19.02], kind: "active" },
  { code: "ETH", name: "Ethiopia", coords: [40.49, 9.15], kind: "active" },
  { code: "ARE", name: "UAE", coords: [53.85, 23.42], kind: "expansion" },
  { code: "SAU", name: "Saudi Arabia", coords: [45.08, 23.89], kind: "expansion" },
  { code: "EGY", name: "Egypt", coords: [30.8, 26.82], kind: "expansion" },
  { code: "MAR", name: "Morocco", coords: [-7.09, 31.79], kind: "expansion" },
  { code: "USA", name: "United States", coords: [-95.7, 37.09], kind: "expansion" },
];

export default function WorldMap({ compact = false }) {
  const { t } = useTranslation();
  const [tip, setTip] = useState(null);

  const fillFor = (a3) => {
    if (HQ_A3.has(a3)) return "#111111";
    if (ACTIVE_A3.has(a3)) return "#D4AF37";
    if (EXPANSION_A3.has(a3)) return "#C5A059";
    return "#EDECE8";
  };

  return (
    <div className="relative w-full" data-testid="world-map">
      <ComposableMap projection="geoEqualEarth" projectionConfig={{ scale: 155 }} style={{ width: "100%", height: compact ? "auto" : "auto" }}>
        <Geographies geography={GEO_URL}>
          {({ geographies }) => geographies.map((geo) => {
            const a3 = geo.id;
            const fill = fillFor(a3);
            const name = NAME_MAP[a3] || geo.properties.name;
            return (
              <Geography
                key={geo.rsmKey}
                geography={geo}
                onMouseEnter={() => (HQ_A3.has(a3) || ACTIVE_A3.has(a3) || EXPANSION_A3.has(a3)) && setTip(name)}
                onMouseLeave={() => setTip(null)}
                style={{
                  default: { fill, stroke: "#fff", strokeWidth: 0.5, outline: "none" },
                  hover: { fill: HQ_A3.has(a3) || ACTIVE_A3.has(a3) || EXPANSION_A3.has(a3) ? "#F5E8B8" : "#E5E4DE", outline: "none", cursor: "pointer" },
                  pressed: { fill: "#A88530", outline: "none" },
                }}
              />
            );
          })}
        </Geographies>
        {MARKERS.map((m) => (
          <Marker key={m.code} coordinates={m.coords} onMouseEnter={() => setTip(m.name)} onMouseLeave={() => setTip(null)}>
            <circle r={m.kind === "hq" ? 4 : 2.5} fill={m.kind === "hq" ? "#111" : m.kind === "active" ? "#D4AF37" : "#C5A059"} stroke="#fff" strokeWidth={1} />
          </Marker>
        ))}
      </ComposableMap>

      <div className="flex flex-wrap items-center gap-4 mt-4 text-xs">
        <LegendDot color="#111" label={t("export.hq")} />
        <LegendDot color="#D4AF37" label={t("export.active")} />
        <LegendDot color="#C5A059" label={t("export.expansion")} />
        {tip && <span className="ml-auto font-display font-semibold text-sm text-gold-600" data-testid="map-tooltip">{tip}</span>}
      </div>
    </div>
  );
}

const LegendDot = ({ color, label }) => (
  <div className="flex items-center gap-2">
    <span className="w-2.5 h-2.5 rounded-full" style={{ background: color }} />
    <span className="text-muted-foreground">{label}</span>
  </div>
);
